import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import {
  Upload,
  Trash2,
  ArrowUp,
  ArrowDown,
  Link as LinkIcon,
  Loader2,
  ImagePlus,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MAX_WIDTH = 1600;
const JPEG_QUALITY = 0.82;

// Resize + compress an image File to a base64 JPEG data URL
const compressImage = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, MAX_WIDTH / img.width);
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL("image/jpeg", JPEG_QUALITY));
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const AdminGallery = () => {
  const token = localStorage.getItem("grizzly_admin_token");
  const authHeader = { Authorization: `Bearer ${token}` };
  const fileInputRef = useRef(null);

  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [showUrl, setShowUrl] = useState(false);
  const [urlValue, setUrlValue] = useState("");
  const [urlAlt, setUrlAlt] = useState("");

  const fetchImages = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/gallery`, {
        headers: authHeader,
      });
      setImages(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchImages();
  }, [fetchImages]);

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        if (!file.type.startsWith("image/")) continue;
        const dataUrl = await compressImage(file);
        const res = await axios.post(
          `${API}/admin/gallery`,
          { src: dataUrl, alt: file.name.replace(/\.[^.]+$/, ""), order: 0 },
          { headers: authHeader },
        );
        setImages((arr) => [...arr, res.data]);
      }
    } catch (err) {
      alert(err.response?.data?.detail || "Erreur lors de l'envoi de l'image");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const addByUrl = async () => {
    if (!urlValue.trim()) return;
    setUploading(true);
    try {
      const res = await axios.post(
        `${API}/admin/gallery`,
        { src: urlValue.trim(), alt: urlAlt.trim(), order: 0 },
        { headers: authHeader },
      );
      setImages((arr) => [...arr, res.data]);
      setUrlValue("");
      setUrlAlt("");
      setShowUrl(false);
    } catch (err) {
      alert(err.response?.data?.detail || "Erreur lors de l'ajout");
    } finally {
      setUploading(false);
    }
  };

  const deleteImage = async (id) => {
    if (!window.confirm("Supprimer cette image de la galerie ?")) return;
    try {
      await axios.delete(`${API}/admin/gallery/${id}`, { headers: authHeader });
      setImages((arr) => arr.filter((im) => im.id !== id));
    } catch (err) {
      console.error(err);
    }
  };

  // Swap order with neighbour
  const move = async (index, dir) => {
    const target = index + dir;
    if (target < 0 || target >= images.length) return;
    const a = images[index];
    const b = images[target];
    try {
      await Promise.all([
        axios.patch(
          `${API}/admin/gallery/${a.id}`,
          { order: b.order },
          { headers: authHeader },
        ),
        axios.patch(
          `${API}/admin/gallery/${b.id}`,
          { order: a.order },
          { headers: authHeader },
        ),
      ]);
      const next = [...images];
      next[index] = { ...b, order: a.order };
      next[target] = { ...a, order: b.order };
      next.sort((x, y) => x.order - y.order);
      setImages(next);
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div data-testid="admin-gallery" className="space-y-8">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-serif text-3xl font-light tracking-tight mb-1">
            Galerie d&apos;images
          </h2>
          <p className="text-sm text-ivory/55">
            {images.length} image(s) · glissez pour ajouter, réordonnez et
            supprimez.
          </p>
        </div>
        <div className="flex gap-3">
          <button
            data-testid="add-url-toggle"
            onClick={() => setShowUrl((s) => !s)}
            className="btn-secondary"
            style={{ padding: "0.7rem 1.2rem", fontSize: "0.7rem" }}
          >
            <LinkIcon size={14} /> Par URL
          </button>
          <button
            data-testid="upload-trigger"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="btn-primary disabled:opacity-60"
            style={{ padding: "0.7rem 1.4rem", fontSize: "0.7rem" }}
          >
            {uploading ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <Upload size={14} />
            )}
            {uploading ? "Envoi..." : "Ajouter des photos"}
          </button>
          <input
            ref={fileInputRef}
            data-testid="file-input"
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(e) => handleFiles(e.target.files)}
          />
        </div>
      </div>

      {/* URL add form */}
      {showUrl && (
        <div
          data-testid="url-form"
          className="bg-surface border border-copper p-5 flex flex-col md:flex-row gap-3 md:items-end"
        >
          <div className="flex-1">
            <label className="overline block mb-2">URL de l&apos;image</label>
            <input
              data-testid="url-input"
              value={urlValue}
              onChange={(e) => setUrlValue(e.target.value)}
              className="input-elegant"
              placeholder="https://..."
            />
          </div>
          <div className="flex-1">
            <label className="overline block mb-2">Légende (optionnel)</label>
            <input
              data-testid="url-alt-input"
              value={urlAlt}
              onChange={(e) => setUrlAlt(e.target.value)}
              className="input-elegant"
              placeholder="Salle du restaurant"
            />
          </div>
          <button
            data-testid="url-add-button"
            onClick={addByUrl}
            disabled={uploading}
            className="btn-primary disabled:opacity-60"
            style={{ padding: "0.7rem 1.4rem", fontSize: "0.7rem" }}
          >
            Ajouter
          </button>
        </div>
      )}

      {/* Dropzone */}
      <div
        data-testid="dropzone"
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          handleFiles(e.dataTransfer.files);
        }}
        onClick={() => fileInputRef.current?.click()}
        className="border border-dashed border-edge hover:border-copper transition-colors cursor-pointer py-10 flex flex-col items-center gap-3 text-ivory/50 hover:text-copper"
      >
        <ImagePlus size={28} strokeWidth={1.5} />
        <p className="text-sm">
          Glissez vos photos ici ou cliquez pour parcourir
        </p>
        <p className="text-xs text-ivory/35">
          JPG, PNG · redimensionnées automatiquement
        </p>
      </div>

      {/* Grid */}
      {loading ? (
        <div className="text-center py-20 text-ivory/50">Chargement...</div>
      ) : images.length === 0 ? (
        <div className="text-center py-20 border border-dashed border-edge text-ivory/40">
          Aucune image. Ajoutez vos premières photos ci-dessus.
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {images.map((img, i) => (
            <div
              key={img.id}
              data-testid={`gallery-admin-item-${img.id}`}
              className="group relative aspect-square overflow-hidden bg-surface border border-edge"
            >
              <img
                src={img.src}
                alt={img.alt}
                className="w-full h-full object-cover"
              />
              {/* overlay */}
              <div className="absolute inset-0 bg-obsidian/70 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-between p-3">
                <div className="flex justify-between items-start">
                  <span className="text-[10px] uppercase tracking-[0.2em] text-copper bg-obsidian/80 px-2 py-1">
                    #{i + 1}
                  </span>
                  <button
                    data-testid={`delete-image-${img.id}`}
                    onClick={() => deleteImage(img.id)}
                    className="p-1.5 bg-obsidian/80 border border-edge text-ivory/70 hover:text-red-400 hover:border-red-400/40 transition-colors"
                    title="Supprimer"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <p className="text-xs text-ivory/80 truncate flex-1">
                    {img.alt || "—"}
                  </p>
                  <div className="flex gap-1">
                    <button
                      data-testid={`move-up-${img.id}`}
                      onClick={() => move(i, -1)}
                      disabled={i === 0}
                      className="p-1.5 bg-obsidian/80 border border-edge text-ivory/70 hover:text-copper hover:border-copper transition-colors disabled:opacity-30"
                      title="Monter"
                    >
                      <ArrowUp size={13} />
                    </button>
                    <button
                      data-testid={`move-down-${img.id}`}
                      onClick={() => move(i, 1)}
                      disabled={i === images.length - 1}
                      className="p-1.5 bg-obsidian/80 border border-edge text-ivory/70 hover:text-copper hover:border-copper transition-colors disabled:opacity-30"
                      title="Descendre"
                    >
                      <ArrowDown size={13} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminGallery;
